<template>
    <q-card class="c-ToolPanelColumns c-ToolPanelColumns_1 q-mx-none full-width q-pa-none" style="width: 100%; display: flex; flex-direction: row; max-width: 650px;" v-if="isvisible" flat>
        <q-card class="q-mx-none full-width q-pa-none" style="width: 100%; flex-grow: 1; display: flex; flex-direction: column; background-color: white;" flat>
            <div class="columns-panel-intro q-py-xs q-mt-none q-mx-sm consistentText">
                {{ Translate('Select the columns to include in the report. Use the search box or the filter icon to narrow the choices.') }}
            </div>

            <OverflowPillGroup
                class="q-py-sm q-mx-sm"
                :removePill="removeColumn"
                :pills="selected_columns"
                :typeDescriptor="Translate('columns')"
                @reorder="onColumnsReorder"
                >
            </OverflowPillGroup>

            <div class="row items-center no-wrap q-mb-md q-mx-sm">
                <q-input bg-color="white" outlined dense hide-bottom-space clearable :label="Translate('Search')" v-model="search" class="col">
                    <template v-slot:prepend>
                        <q-icon name="mdi-magnify" />
                    </template>
                </q-input>
                <q-btn dense flat icon="mdi-filter" style="color: var(--v-secondaryText-base);" class="col-auto q-ml-sm filter-action-btn">
                    <q-badge v-if="anyFilters" color="primary" rounded floating />
                    <q-menu anchor="bottom right" self="top right" :offset="[0, 8]" v-model="filter_menuopen" style="overflow: hidden;">
                        <q-card @mouseleave="filter_menuopen = false" class="q-pa-xs" style="min-width: 400px; max-height: calc(65vh - 8px); overflow: hidden;">
                            <MetricTypeFilter :debugMode="debugMode"
                                            :datatypes="columnDataTypes"
                                            :initial_datatypes_selection="columnDataTypes_selection"
                                            @datatypes_changed="(values) => columnDataTypes_selection = values"
                                            :categories="columnCategories"
                                            :initial_category_selection="columnCategory_selection"
                                            @categories_changed="(values) => columnCategory_selection = values"
                                            @anyfilters="(value) => anyFilters = value">
                            </MetricTypeFilter>
                        </q-card>
                    </q-menu>
                </q-btn>
            </div>
            <div v-show="!search" v-for="(cat,k) in filterCategories" :key="k">
                <div class="q-px-sm q-py-xs consistentText" v-if="isCategoryVisible(cat)" style="border-bottom: 2px solid var(--v-lightGreyBorder-base);">{{ Translate(filterCategoryPrefix(cat)).toUpperCase() }}</div>

                <ColumnItemList v-if="isCategoryVisible(cat)"
                                :ReadOnly="ReadOnly"
                                :items="getColumnsByCategory(cat)"
                                :columnLookup="column_lookup"
                                :column_default_visibility="column_default_visibility"
                                :showHelp="showHelp"
                                :addColumn="addColumn"
                                :removeColumn="removeColumn"
                                :getColumnHelp="getColumnHelp"
                                @setvisible="setcolumnvisible"
                                @setdefaulthidden="setdefaulthidden"></ColumnItemList>

            </div>

            <div v-if="search">
                <div class="q-px-sm q-py-xs consistentText" style="border-bottom: 2px solid var(--v-lightGreyBorder-base);">
                    {{ Translate('SEARCH RESULTS') }}
                </div>

                <ColumnItemList :items="search_results"
                                :ReadOnly="ReadOnly"
                                :columnLookup="column_lookup"
                                :column_default_visibility="column_default_visibility"
                                :showHelp="showHelp"
                                :addColumn="addColumn"
                                :removeColumn="removeColumn"
                                :getColumnHelp="getColumnHelp"
                                @setvisible="setcolumnvisible"
                                @setdefaulthidden="setdefaulthidden"></ColumnItemList>
            </div>

        </q-card>

        <q-dialog v-model="help.visible">
            <q-card style="max-width: 590px; width: 100%;">
                <q-card-section class="text-h5">
                    {{ help.item ? Translate(help.item.Title) : '' }}
                </q-card-section>

                <q-card-section v-html="help.item?.Description || ''" />

                <q-card-actions>
                    <q-space></q-space>

                    <q-btn color="primary"
                           flat
                           no-caps
                           class="c-SentenceCaseButton"
                           @click="help.visible = false">
                        {{ Translate('Close') }}
                    </q-btn>
                </q-card-actions>
            </q-card>
        </q-dialog>
    </q-card>
</template>

<script>
    import helps from '../../../careHelpfulFunctions.jsx';
    import utils from '@/Shared/utils.jsx';
    import common from './common.js';
    import methods from '@/Shared/methods';

    import ColumnItemList from './columnItemList.vue';
    import MetricTypeFilter from './metricTypeFilterPopup.vue';
    import OverflowPillGroup from './overflowPillGroup.vue';

    import { appSettings } from '@/Shared/appSettings';

    export default {
        components: {
            ColumnItemList,
            MetricTypeFilter,
            OverflowPillGroup,
        },
        props: {
            ReadOnly: null,
            isvisible: null,
            storageKey: null,
            column_list: {
                type: Array
            },
            source: null,
            settings: null,
            columnDefs: {
                type: Array
            },
            column_default_visibility: null,
            setColumnsDefaultHidden: null,
            setColumnsVisible: null,
            debugMode: null,
            column_order: Array,
        },
        data: function () {
            return {
                categoryPanels: {},
                search: '',
                filter_menuopen: false,
                category_selection: [],
                categories: [ { text: 'Categories', divider: true } ],
                column_lookup: {},

                columnDataTypes: [
                    { text: 'Counts', filter: 'type', types: ['countColumn', 'floatColumn'] },
                    { text: 'Dates / Times', filter: 'type', types: ['dateColumn', 'dateOnlyColumn', 'timeOnlyColumn'] },
                    { text: 'Durations', filter: 'type', types: ['durationColumn'] },
                    { text: 'Percents', filter: 'type', types: ['percentColumn'] },
                    { text: 'Text Values', filter: 'type', types: ['textSetColumn', 'searchableTextColumn', 'channelTypeColumn', 'directionColumn', 'userIDColumn', 'customerIDColumn', 'phoneNumColumn'] },
                    { text: 'True / False', filter: 'type', types: ['booleanColumn'] },
                    { text: 'Sparkline', filter: 'type', types: ['sparklineColumn'] }
                ],
                columnCategories: [],
                //columnSources: [],

                columnDataTypes_selection: [],
                columnCategory_selection: [],
                columnSources_selection: [],

                anyFilters: false,

                help: {
                    visible: false,
                    item: {}
                },
                isReorderingColumns: false,
                selected_columns: [],
            }
        },
        watch: {
            columnDataTypes_selection: function (newv) {
                this.saveColumnFilters();
            },
            columnCategory_selection: function (newv) {
                this.saveColumnFilters();
            },
            columnSources_selection: function (newv) {
                this.saveColumnFilters();
            },
            visibleOrderedIds: {
                immediate: true,
                handler() {
                    // Ignore if the change originated from dragging a pill in the panel
                    if (this.isReorderingColumns) {
                        this.isReorderingColumns = false;
                        return;
                    }
                    
                    let ordered = [];
                    if (this.column_order) {
                        ordered = this.column_order
                            .map(colId => this.columnDefs.find(c => c.colId === colId))
                            .filter(c => c && !c.hide && !c.suppressColumnsToolPanel);
                    }
                    
                    const orderedIds = ordered.map(c => c.colId);
                    const remaining = this.columnDefs.filter(a => a.colId && !a.hide && !a.suppressColumnsToolPanel && !orderedIds.includes(a.colId));
                    
                    this.selected_columns = [...ordered, ...remaining];
                }
            }
        },
        created() {
            console.log(`loading ToolReportColumns _uid:${this._uid} with ${this.column_list?.length} column(s); ${this.columnDefs.length} column def(s)`);

            for (let cat of [...new Set(this.column_list.filter(a => a.ColumnUse.Filter || a.ColumnUse.Group || a.ColumnUse.Computed).map(a => a.FilterCategory || a.Category))])
                this.categoryPanels[cat] = [];

            this.categories = [
                ...this.categories,
                ...this.filterCategories.map(a => ({ text: a, category: a }))
            ];
            this.category_selection = this.filterCategories.map((a, i) => i);

            this.column_lookup = helps.toLookup(this.columnDefs, 'colId');

            //------------------------ New --------------------//

            // Populate a distinct list of categories for metric filters
            this.columnCategories = [...new Set(this.column_list.filter(a => a.ColumnUse.Filter || a.ColumnUse.Group || a.ColumnUse.Computed).map(a => a.FilterCategory || a.Category))]
                .filter(a => a)
                .toSorted((a, b) => common.byField(a, b))
                .map(a => ({ text: a.indexOf('#') <= 1 ? a.substr(a.indexOf('#') + 1) : a, filter: 'Category', category: a }));

            this.loadColumnFilters();
        },
        computed: {
            filterCategories: function () {
                return [...new Set(this.column_list.filter(a => a.ColumnUse.Filter || a.ColumnUse.Group || a.ColumnUse.Computed).map(a => a.FilterCategory || a.Category))]
                    .toSorted((a, b) => common.byField(a, b));
            },
            search_results: function () {
                const s = this.search.toLowerCase();
                return this.column_list
                    .filter(a =>
                        (a.ColumnUse.Filter || a.ColumnUse.Group || a.ColumnUse.Computed) && !a.CompoundFilter && (
                        a.ColId.toLowerCase().includes(s) ||
                        a.Title.toLowerCase().includes(s) ||
                        a.Tooltip?.toLowerCase().includes(s) ||
                        a.Description?.toLowerCase().includes(s) ||
                        a.SourceType?.toLowerCase().includes(s)
                        )
                    ).toSorted((a, b) => common.byField(a, b, 'Title'));
            },
            visibleOrderedIds() {
                let ordered = [];
                if (this.column_order) {
                    ordered = this.column_order.filter(colId => {
                        const c = this.columnDefs.find(c => c.colId === colId);
                        return c && !c.hide && !c.suppressColumnsToolPanel;
                    });
                }
                const remaining = this.columnDefs
                    .filter(a => a.colId && !a.hide && !a.suppressColumnsToolPanel && !ordered.includes(a.colId))
                    .map(a => a.colId);
                
                return [...ordered, ...remaining].join(',');
            }
        },
        methods: {
            ...methods,
            loadColumnFilters() {
                for (let i = 0; i < this.columnDataTypes.length; i++)
                    if (appSettings.LocalStorage(this.storageKey + '-columnFilters-type:' + this.columnDataTypes[i].flat + '-' + (this.source ? `-${this.source}` : '')) != 'hidden')
                        this.columnDataTypes_selection.push(i);

                for (let i = 0; i < this.columnCategories.length; i++)
                    if (appSettings.LocalStorage(this.storageKey + '-columnFilters-type:' + this.columnCategories[i].flat + '-' + (this.source ? `-${this.source}` : '')) != 'hidden')
                        this.columnCategory_selection.push(i);

                this.anyFilters =
                    this.columnDataTypes_selection.length < this.columnDataTypes.length ||
                    this.columnCategory_selection.length < this.columnCategories.length;
            },
            saveColumnFilters() {
                for (let i = 0; i < this.columnDataTypes.length; i++)
                    appSettings.LocalStorage(this.storageKey + '-columnFilters-type:' + this.columnDataTypes[i].flat + '-' + (this.source ? `-${this.source}` : ''), this.columnDataTypes_selection.includes(i) ? null : 'hidden');

                for (let i = 0; i < this.columnCategories.length; i++)
                    appSettings.LocalStorage(this.storageKey + '-columnFilters-type:' + this.columnCategories[i].flat + '-' + (this.source ? `-${this.source}` : ''), this.columnCategory_selection.includes(i) ? null : 'hidden');
            },
            getColumnsByCategory(category) {
            //    return this.column_list.filter(a => (a.ColumnUse.Filter || a.ColumnUse.Group) && (a.FilterCategory || a.Category) == category && !a.CompoundFilter)
            //        .toSorted((a, b) => common.byField(a, b, 'Title'));
                //debugger;
                const visibletypes = this.columnDataTypes_selection.flatMap(i => this.columnDataTypes[i].types);
                //const visiblesources = this.columnSources_selection.map(i => this.columnSources[i].source);
                const cols = this.column_list
                    .filter(a => (a.ColumnUse.Filter || a.ColumnUse.Group || a.ColumnUse.Computed) && !a.CompoundFilter && (a.FilterCategory || a.Category) == category && visibletypes.includes(a.SourceType))
                    .toSorted((a, b) => common.byField(a, b, 'Title'));
                return cols;
            },
            isCategoryVisible(category) {
                const selected = this.columnCategory_selection.map(i => this.columnCategories[i].category);
                return selected.some(a => a == category);
            },
            setPanelState(cat, value) {
                console.log(`setPanelState(cat:${cat},value:${JSON.stringify(value)}); ${typeof value}`);

                const arr = this.categoryPanels[cat];
                arr.splice(0, arr.length);

                for (let i of value)
                    arr.push(i);

                console.log(`setPanelState(cat:${cat}) arr: ${JSON.stringify(arr)}; ${typeof value}`);
            },
            filterCategoryPrefix(cat) {
                const idx = cat.indexOf('#');
                return idx >= 0 && idx < 3 ? cat.substring(idx + 1) : cat;
            },

            setdefaulthidden(col, hidden) {
                this.setColumnsDefaultHidden(col.ColId, hidden);
            },
            setcolumnvisible(col, value) {
                this.setColumnsVisible([col.ColId], value);
            },
            showHelp(col) {
                this.help.item = col;
                this.help.visible = true;
            },
            addColumn(col) {

            },
            removeColumn(col) {
                this.setcolumnvisible({ColId: col.colId}, false)
            },
            getColumnHelp(col) {
                return this.Translate(col.Tooltip || col.Title || 'No title set');
            },
            onColumnsReorder(newCols) {
                this.isReorderingColumns = true;
                this.selected_columns = newCols;
                this.$emit('columnsordered', newCols);
            },
        }
    }
</script>

<style scoped>
    .columns-panel-intro {
        align-self: flex-start !important;
        width: 100%;
    }
    .consistentText {
        color: var(--v-black-base) !important; 
        opacity: 0.8 !important; 
        font-size: 14px !important; 
        text-rendering: optimizelegibility !important; 
        letter-spacing:0.1 !important;
    }
    .filter-action-btn {
        flex-shrink: 0;
    }
    .filter-action-btn :deep(.q-badge--floating) {
        top: auto;
        bottom: 4px;
    }
</style>